import { CommentInput } from 'components/Comments'
import { IComment, IHandleCreateComment, RootState } from 'constants/interfaces'
import React, { memo, useCallback, useMemo } from 'react'
import { MessagePreview } from '../MessagePreview'
import { Box } from '@chakra-ui/core'
import { extractRefComment, getObjectType } from '../utils'
import { Post } from 'redux/reducers/feed'
import keys from 'constants/keys'
import { AppDispatch } from 'config/redux'
import { useDispatch, useSelector } from 'react-redux'
import { setCommentsCounter, updateComment } from 'redux/actions/comments'
import { setCommentContext } from 'redux/actions/feed'

interface IProps {
  chat: Post
  comments: IComment[]
  commentContext: RootState['feed']['commentContext']
  sendComment: (params: {
    id: string
    comment: string
    rasID: string
    createdByUserId: string | undefined
    images?: any[]
    videos?: any[]
    audios?: any
    objectType: number
    files?: any[]
    is_update_post: boolean
    isChat: boolean
    ref_comment?: any
  }) => Promise<string | undefined>
  sendCommentLoading: boolean
  commentsCounter: number
  isShowCommentInput: boolean
}

export const ChatModalFooter = memo(
  ({
    chat,
    comments,
    commentContext,
    sendComment,
    sendCommentLoading,
    commentsCounter,
    isShowCommentInput,
  }: IProps) => {
    const dispatch: AppDispatch = useDispatch()
    const { uid } = useSelector((state: RootState) => state.auth)

    const { post_id: chatId, ras_id: rasID, created_by, groups, tags, users } = chat

    const commentContextCommentId = commentContext.commentId
    const commentContextIsEdit = commentContext.isEdit

    const replyingComment = useMemo(
      () => comments.find((comment) => comment.cid === commentContextCommentId),
      [comments, commentContextCommentId]
    )

    const replyingCommentMessage = replyingComment?.message

    const refComment = useMemo(() => extractRefComment(replyingComment), [replyingComment])

    const initialCommentText = useMemo(
      () => (commentContextIsEdit ? replyingCommentMessage : '') || '',
      [commentContextIsEdit, replyingCommentMessage]
    )

    const handleCloseReply = useCallback(() => {
      dispatch(setCommentContext({ commentId: null, isEdit: false }))
    }, [dispatch])

    const handleCreateMessage = useCallback(
      async ({ comment, images, videos, audios, files }: IHandleCreateComment) => {
        if (!chat) return
        handleCloseReply()

        const objectType = refComment ? keys.COMMENT_OBJECT_TYPE.REF_COMMENT : getObjectType(chat.type)

        const commentID = await sendComment({
          id: chatId,
          comment,
          rasID: rasID || '',
          createdByUserId: uid,
          images,
          videos,
          audios,
          objectType,
          files,
          is_update_post: true,
          isChat: true,
          ref_comment: refComment,
        })

        if (commentID) {
          dispatch(setCommentsCounter({ count: commentsCounter + 1, key: chatId }))
        }
      },
      [chat, handleCloseReply, refComment, sendComment, chatId, rasID, uid, dispatch, commentsCounter]
    )

    const handleEditMessage = useCallback(
      async ({ comment }: IHandleCreateComment) => {
        if (!chat) return
        handleCloseReply()
        dispatch(
          updateComment({
            oid: chat.post_id,
            cid: commentContextCommentId!,
            reaction: '',
            object_created_by: created_by,
            groups: groups,
            tags: tags,
            users: users,
            message: comment,
          })
        )
      },
      [chat, handleCloseReply, dispatch, commentContextCommentId, created_by, groups, tags, users]
    )

    const targetAction = useMemo(
      () => (commentContextIsEdit ? handleEditMessage : handleCreateMessage),
      [commentContextIsEdit, handleEditMessage, handleCreateMessage]
    )

    if (!isShowCommentInput) {
      return null
    }

    return (
      <Box p="10px" w="100%">
        {replyingComment && <MessagePreview comment={replyingComment} onClose={handleCloseReply} />}
        <CommentInput
          postID={chatId}
          isChat
          handleCreateComment={targetAction}
          sendCommentLoading={sendCommentLoading}
          isFocused={false}
          initialCommentText={initialCommentText}
        />
      </Box>
    )
  }
)
